<script setup>
import { computed, ref } from 'vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click', 'selectTicket', 'deselectTicket']);

const hovered = ref(false);

const onSelectTicket = checked => {
  if (checked) {
    emit('selectTicket', props.ticket.display_id);
  } else {
    emit('deselectTicket', props.ticket.display_id);
  }
};

const priorityConfig = {
  urgent: {
    label: 'TICKETS.PRIORITY.URGENT',
    class: 'bg-n-ruby-9/20 text-n-ruby-11',
  },
  high: {
    label: 'TICKETS.PRIORITY.HIGH',
    class: 'bg-n-amber-9/20 text-n-amber-11',
  },
  medium: {
    label: 'TICKETS.PRIORITY.MEDIUM',
    class: 'bg-n-amber-9/10 text-n-amber-11',
  },
  low: {
    label: 'TICKETS.PRIORITY.LOW',
    class: 'bg-n-teal-9/20 text-n-teal-11',
  },
};

const statusConfig = {
  open: 'bg-n-brand/10 text-n-blue-text',
  in_progress: 'bg-n-amber-9/10 text-n-amber-11',
  escalated: 'bg-n-ruby-9/10 text-n-ruby-11',
  waiting_client: 'bg-n-amber-9/20 text-n-amber-11',
  waiting_internal: 'bg-n-amber-9/20 text-n-amber-11',
  resolved: 'bg-n-teal-9/10 text-n-teal-11',
  closed: 'bg-n-slate-9/10 text-n-slate-11',
  cancelled: 'bg-n-slate-9/10 text-n-slate-11',
};

const priority = computed(
  () => priorityConfig[props.ticket.priority] || priorityConfig.low
);
const statusClass = computed(
  () => statusConfig[props.ticket.status] || statusConfig.open
);

const lastActivityTime = computed(() => {
  const date =
    props.ticket.last_activity_at ||
    props.ticket.updated_at ||
    props.ticket.created_at;
  return date ? dynamicTime(new Date(date).getTime() / 1000) : '';
});

const assigneeName = computed(() => props.ticket.assignee?.name || '');
const selectionAvatarName = computed(
  () => assigneeName.value || props.ticket.folio || '?'
);
</script>

<template>
  <div
    class="flex gap-3 px-4 py-3 border-b border-n-weak cursor-pointer transition-colors"
    :class="{
      'bg-n-alpha-2': isActive,
      'hover:bg-n-alpha-1': !isActive,
      'bg-n-slate-2 dark:bg-n-slate-3': selected,
    }"
    @click="$emit('click', ticket)"
  >
    <div class="flex gap-3 flex-1 min-w-0">
      <!-- Selection area (avatar/icon with checkbox overlay) -->
      <div
        class="relative flex-shrink-0"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
        @click.stop
      >
        <Avatar
          :name="selectionAvatarName"
          :size="32"
          rounded-full
        >
          <template #overlay="{ size }">
            <label
              v-if="hovered || selected"
              class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
              :style="{ width: `${size}px`, height: `${size}px` }"
              @click.stop
            >
              <input
                :value="selected"
                :checked="selected"
                class="!m-0 cursor-pointer"
                type="checkbox"
                @change="onSelectTicket($event.target.checked)"
              />
            </label>
          </template>
        </Avatar>
      </div>
      <!-- Content -->
      <div class="flex flex-col gap-1.5 flex-1 min-w-0">
        <!-- Row 1: Folio + Priority + Time -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-xs font-mono text-n-slate-11">{{
              ticket.folio
            }}</span>
            <span
              class="px-1.5 py-0.5 text-xs rounded-full"
              :class="priority.class"
            >
              {{ $t(priority.label) }}
            </span>
          </div>
          <span class="text-xs text-n-slate-10">{{ lastActivityTime }}</span>
        </div>

        <!-- Row 2: Title -->
        <p class="text-sm font-medium text-n-slate-12 truncate">
          {{ ticket.title }}
        </p>

        <!-- Row 3: Status + Assignee -->
        <div class="flex items-center justify-between">
          <span class="px-1.5 py-0.5 text-xs rounded-full" :class="statusClass">
            {{ $t(`TICKETS.STATUS.${ticket.status?.toUpperCase()}`) }}
          </span>
          <div v-if="assigneeName" class="flex items-center gap-1.5">
            <Avatar :name="assigneeName" :size="16" />
            <span class="text-xs text-n-slate-11 truncate max-w-[100px]">
              {{ assigneeName }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
